import classNames from 'classnames'
import React, { forwardRef, useEffect } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { RichText, RichTextProps } from '@src/quince-components/src'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import Swiper from '../../atoms/swiper'
import { NAV_POSITION } from '../../atoms/swiper/Swiper'
import { TrackConfig } from '../../hocs/withTracking'

import styles from './productListSectionThree.module.scss'

interface ProductListSectionProps {
  preHeadingRichTextProps?: RichTextProps
  productCards: IProductCard[]
  wrapperClassName?: string
  productCardDesktopClassName?: string
  productCardMobileClassName?: string
  hideProductOptions?: boolean
  hideProductReviews?: boolean
  navPosition?: NAV_POSITION
  title?: string
  tracker?: string
  productClickTrackConfig?: TrackConfig
  numberOfSlidesToScroll?: number
}

const ProductListSectionThree = forwardRef<HTMLDivElement, ProductListSectionProps>(
  function ProductListSectionThreeEl(props, ref) {
    const {
      preHeadingRichTextProps,
      productCards,
      wrapperClassName,
      productCardDesktopClassName,
      productCardMobileClassName,
      hideProductOptions,
      hideProductReviews,
      title,
      tracker,
      productClickTrackConfig,
    } = props
    const router = useRouter()
    const deviceInfo = useDeviceInfo()
    const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

    const productViewTracker = tracker && `${tracker}__productListSectionThree`

    const maxWidth = getMaxWidthInPx({
      maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
      maxWidthDesktopInRem: styles.productCardWidth ?? '',
      deviceType: deviceInfo.deviceType,
    })

    const productCardClassName = isDesktop
      ? classNames(styles.productCard, productCardDesktopClassName)
      : classNames(styles.productCardWrapperMobile, productCardMobileClassName)

    useEffect(() => {
      if (productViewTracker) {
        batchedAnalytics.initializeBatchedAnalyticsEvent({
          tracker: productViewTracker,
          eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
          debouncePeriod: 1000,
        })
        batchedAnalytics.trackBatchedAnalytics({
          id: `internalAnalytics-${productViewTracker}`,
          tracker: productViewTracker,
          callback: ({ events }) => {
            const productList = events.map(event => getProductDataForBatchedAnalytics(event))
            const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
            trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
              productList: productList,
              tagsCount: tagsCount,
              ...productClickTrackConfig?.context,
            })
          },
        })
      }
    }, [productViewTracker, productClickTrackConfig?.context])

    return (
      <div id={title?.toLowerCase()} className={classNames(styles.wrapper, wrapperClassName ?? '')} ref={ref}>
        {preHeadingRichTextProps && (
          <RichText
            {...preHeadingRichTextProps}
            className={classNames(styles.header, preHeadingRichTextProps.className)}
          />
        )}

        <Swiper
          id="product-list-three-carousel"
          className={styles.productList}
          variableWidth
          showNavigator={isDesktop}
          numberOfVisibleSlides={isDesktop ? 4 : 1}
          tracker={tracker}
        >
          {productCards.map((productCard, idx) => {
            const initialSelectedVariant = getInitialSelectedVariant({
              cardVariants: productCard.product.cardVariants,
            })
            return (
              <ProductCard
                key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                shouldShowMore
                onShowMoreClick={({ selectedVariant }) => {
                  navigateToPDP({
                    slug: productCard.product.slug,
                    tracker: tracker,
                    selectedOption: selectedVariant?.displayConfig.value,
                    router,
                  })
                }}
                className={classNames(
                  productCardClassName,
                  idx === productCards.length - 1 && styles.productCard__lastElement,
                )}
                productCard={productCard}
                hideReviews={hideProductReviews}
                hideOptions={hideProductOptions}
                tracker={tracker ? `${tracker}__0__${idx}` : undefined}
                maxWidth={maxWidth}
                showMScroll={false}
                productViewTracker={productViewTracker}
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                productClickTrackConfig={
                  productClickTrackConfig
                    ? {
                        ...productClickTrackConfig,
                        context: {
                          ...productClickTrackConfig.context,
                          row: 0,
                          column: idx,
                          product_id: productCard.product.relatedIds.shopifyId,
                          product_color: initialSelectedVariant?.displayConfig.value,
                          contentContainerType:
                            productClickTrackConfig.context?.contentContainerType ||
                            CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY,
                          contentContainerName:
                            productClickTrackConfig.context?.contentContainerName ||
                            CUSTOM_SECTION_CONTAINER_NAMES.INFINITE_LIST,
                          contentContainerDisplayName:
                            productClickTrackConfig.context?.contentContainerDisplayName ||
                            'Product List Section Three',
                        },
                      }
                    : undefined
                }
              />
            )
          })}
        </Swiper>
      </div>
    )
  },
)
export default ProductListSectionThree
